import ComposePlugins, { addComposePlugins } from './ComposePlugins';
import ContentOperationPlugin from './contentOperation/ContentOperationPlugin';
import switchToReadWriteRecipient from '../actions/switchToReadWriteRecipient';
import canModifyRecipients from '../utils/canModifyRecipients';
import createAttachments from '../utils/createAttachments';
import {
    ComposeViewStateAdapter,
    createPluginViewStateAdapter,
} from '../utils/createPluginViewStateAdapter';
import deletePendingInlineAttachment from '../utils/deletePendingInlineAttachment';
import fixInlineImageSrc from '../utils/fixInlineImageSrc';
import { getRecipientWellsFromComposeViewState } from '../utils/getAllRecipientsAsEmailAddressStrings';
import getCurrentFromAddress from '../utils/getFromAddressFromRecipientWell';
import isSmartComposeEnabled from '../utils/isSmartComposeEnabled';
import { AddinEditorPlugin } from 'owa-addins-editor-plugin';
import { AttachmentFile, AttachmentFileList } from 'owa-attachment-file-types';
import { CreateAttachmentsProperties, InlineAttachmentState } from 'owa-attachment-well-data';
import { LazyCortanaPickerPluginClass } from 'owa-cortana-compose';
import { LazyAccCheckerPluginClass } from 'owa-editor-acc-checker-plugin';
import { LazyAugLoopPluginClass } from 'owa-editor-augloop-plugin';
import { LazyDictationPluginClass } from 'owa-editor-dictation-plugin';
import { createDockingPlugin } from 'owa-editor-docking-plugin';
import { LazyEmojiPickerPluginClass } from 'owa-editor-emoji-picker-plugin';
import { LazyForgottenAttachmentsPluginClass } from 'owa-editor-forgotten-attachments-plugin';
import { LazyKeyStrokeCounterPluginClass } from 'owa-editor-keystroke-counter-plugin';
import { LazyLinkPluginClass } from 'owa-editor-link-plugin';
import { LazyAtMentionsPickerPluginClass } from 'owa-editor-mentions-picker-plugin';
import EditorOperationPlugin from 'owa-editor-operation-plugin/lib/plugin/EditorOperationPlugin';
import { LazyPasteOptionPluginClass } from 'owa-editor-paste-option-plugin';
import { getAnnotationHandlerAsync as getProofingAnnotationHandlerAsync } from 'owa-editor-proofing-handler';
import RibbonPlugin from 'owa-editor-ribbonplugin/lib/plugin/RibbonPlugin';
import { LazySmartSuggestionsPluginClass } from 'owa-editor-smart-suggestions-plugin';
import { LazySmartComposePluginClass } from 'owa-editor-smartcompose-plugin';
import { LazyTableEditPluginClass } from 'owa-editor-table-edit-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import { LazyLinkPreviewPluginClass } from 'owa-link-preview-compose';
import { ComposeViewState } from 'owa-mail-compose-store';
import {
    isPragueEnabled,
    PragueEnabledScenario,
    LazyPraguePluginClass,
} from 'owa-prague-integrations';
import getRecipientsFromRecipientWells from 'owa-recipient-common/lib/utils/getRecipientsFromRecipientWells';
import OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import UserOptionsType from 'owa-service/lib/contract/UserOptionsType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    InlineImagePlugin,
    LazyInlineImageResizePluginClass,
    CreateAttachments,
    DeletePendingInlineAttachment,
    lazyGetSelectedImage,
} from 'owa-inline-image';

function getCreateAttachmentsHandler(adapter: ComposeViewStateAdapter): CreateAttachments {
    return function(
        files: AttachmentFileList,
        isInline: boolean,
        shouldShare: boolean,
        isHiddenInline: boolean,
        processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void
    ) {
        const baseProperties = { isInline, isHiddenInline };
        const createAttachmentsProperties: CreateAttachmentsProperties = shouldShare
            ? { ...baseProperties, shouldShare: true }
            : { ...baseProperties, shouldShare: false };
        createAttachments(
            files,
            adapter(),
            createAttachmentsProperties,
            processInlineImageBeforeUploadFunc
        );
    };
}

function getDeleteInlineAttachmentsHandler(
    adapter: ComposeViewStateAdapter
): DeletePendingInlineAttachment {
    return function(placeholderId: string) {
        deletePendingInlineAttachment(placeholderId, adapter());
    };
}

function getAttachmentStatesHandler(
    adapter: ComposeViewStateAdapter
): () => InlineAttachmentState[] {
    return () => adapter().attachmentWell.inlineAttachments;
}

function getSubjectHandler(adapter: ComposeViewStateAdapter): () => string {
    return () => adapter().subject;
}

export default function createComposePlugins(viewState: ComposeViewState): ComposePlugins {
    const viewStateAdapter = createPluginViewStateAdapter(viewState);

    // PasteManager depends on InlineImagePlugin to attach inline images.
    const inlineImagePlugin = new InlineImagePlugin(
        getAttachmentStatesHandler(viewStateAdapter),
        getCreateAttachmentsHandler(viewStateAdapter),
        getDeleteInlineAttachmentsHandler(viewStateAdapter),
        fixInlineImageSrc(viewState)
    );

    const getAllRecipientsFunc = (adapter: ComposeViewStateAdapter) => () => {
        return getRecipientsFromRecipientWells(getRecipientWellsFromComposeViewState(adapter()));
    };

    const getAllRecipientWellsFunc = (adapter: ComposeViewStateAdapter) => () => {
        return getRecipientWellsFromComposeViewState(adapter());
    };

    const switchToReadWriteRecipientWell = (adapter: ComposeViewStateAdapter) => () => {
        if (viewState.showCompactRecipientWell) {
            switchToReadWriteRecipient(adapter());
        }
    };

    const userConfiguration: OwaUserConfiguration = getUserConfiguration();
    const userOptions: UserOptionsType = userConfiguration && userConfiguration.UserOptions;
    const isSmartReplyOptionEnabled: boolean =
        userOptions && userOptions.WebSuggestedRepliesEnabledForUser;
    const isRtl = userConfiguration.SessionSettings.IsUserCultureRightToLeft;

    const composePlugins: ComposePlugins = {
        docking: createDockingPlugin(createPluginViewStateAdapter(viewState, 'docking')),
        ribbon: new RibbonPlugin(
            createPluginViewStateAdapter(viewState, 'ribbon'),
            lazyGetSelectedImage.import(),
            viewState.composeId
        ),
        inlineImage: inlineImagePlugin,
        inlineImageResize: new LazyInlineImageResizePluginClass(),
        mentionsPicker: new LazyAtMentionsPickerPluginClass(
            createPluginViewStateAdapter(viewState, 'mentionsPicker'),
            viewState.toRecipientWell,
            switchToReadWriteRecipientWell(viewStateAdapter),
            canModifyRecipients(viewState),
            getAllRecipientsFunc(viewStateAdapter)
        ),
        cortanaCompose: isFeatureEnabled('mc-cortanaCompose') && new LazyCortanaPickerPluginClass(),
        emojiPicker: new LazyEmojiPickerPluginClass(
            createPluginViewStateAdapter(viewState, 'emojiPicker'),
            true /* useFlexPane */,
            viewState.composeId
        ),
        contentOperation: new ContentOperationPlugin(viewStateAdapter),
        smartSuggestions: isFeatureEnabled('mc-smartSuggestions')
            ? new LazySmartSuggestionsPluginClass(
                  getAllRecipientWellsFunc(viewStateAdapter),
                  viewState.composeId
              )
            : null,
        linkPreview: new LazyLinkPreviewPluginClass(),
        addin: isFeatureEnabled('rp-addins')
            ? new AddinEditorPlugin(createPluginViewStateAdapter(viewState, 'addin'))
            : null,
        pasteOption: new LazyPasteOptionPluginClass(
            createPluginViewStateAdapter(viewState, 'pasteOption'),
            isRtl
        ),
        tableEdit: new LazyTableEditPluginClass(
            createPluginViewStateAdapter(viewState, 'tableEdit')
        ),
        accChecker: new LazyAccCheckerPluginClass(
            createPluginViewStateAdapter(viewState, 'accChecker')
        ),
        link: new LazyLinkPluginClass(
            viewState.attachmentWell,
            getAllRecipientWellsFunc(viewStateAdapter),
            getCurrentFromAddress(viewState.toRecipientWell)
        ),
        smartCompose: isSmartComposeEnabled()
            ? new LazySmartComposePluginClass(
                  getAllRecipientWellsFunc(viewStateAdapter),
                  getSubjectHandler(viewStateAdapter),
                  viewState.operation,
                  createPluginViewStateAdapter(viewState, 'smartCompose')
              )
            : null,
        prague: isPragueEnabled(PragueEnabledScenario.Compose) ? new LazyPraguePluginClass() : null,
        augLoop:
            isFeatureEnabled('mc-augmentationLoop') || isFeatureEnabled('mc-officeEditorBX')
                ? new LazyAugLoopPluginClass([getProofingAnnotationHandlerAsync])
                : null,
        dictation: isFeatureEnabled('cmp-dictation')
            ? new LazyDictationPluginClass(viewStateAdapter)
            : null,
        editorOperation: new EditorOperationPlugin(),
        keyStrokeCounter:
            isFeatureEnabled('mc-smartReply') && isSmartReplyOptionEnabled
                ? new LazyKeyStrokeCounterPluginClass(
                      createPluginViewStateAdapter(viewState, 'keyStrokeCounter')
                  )
                : null,
        forgottenAttachments: isFeatureEnabled('doc-attachment-forgottenAttachments')
            ? new LazyForgottenAttachmentsPluginClass(
                  createPluginViewStateAdapter(viewState, 'forgottenAttachments')
              )
            : null,
    };

    // Setting the current compose plugins to the newly created one
    addComposePlugins(viewState, composePlugins);
    return composePlugins;
}
